import type { TourStepPlacement } from './types';

export type RectLike = Pick<DOMRect, 'top' | 'left' | 'width' | 'height' | 'right' | 'bottom'>;

function isElementVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 1 || rect.height <= 1) return false;

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
    return false;
  }

  // `offsetParent === null` is normal for fixed/sticky (and their descendants) — still visible.
  if (el.offsetParent === null) {
    let node: HTMLElement | null = el;
    let inFixedTree = style.position === 'fixed' || style.position === 'sticky';
    while (node && !inFixedTree) {
      node = node.parentElement;
      if (!node || node === document.body || node === document.documentElement) break;
      const parentStyle = window.getComputedStyle(node);
      if (parentStyle.display === 'none') return false;
      if (parentStyle.position === 'fixed' || parentStyle.position === 'sticky') {
        inFixedTree = true;
      }
    }
    if (!inFixedTree) return false;
  }

  return true;
}

export function queryTourTarget(tourId: string): HTMLElement | null {
  const selector = `[data-tour-id="${CSS.escape(tourId)}"]`;
  const all = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
  if (all.length === 0) return null;

  // Prefer visible element (handles responsive duplicates where one is display:none).
  const visible = all.find(isElementVisible);
  return visible ?? all[0] ?? null;
}

export function getTargetRect(target: HTMLElement): RectLike {
  return target.getBoundingClientRect();
}

/**
 * Scroll target into view without recentering horizontally — `inline: 'center'`
 * often expands overflow / shifts sticky header layout in the v2 shell.
 */
export function scrollTargetIntoView(target: HTMLElement): void {
  try {
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  } catch {
    // ignore
  }
}

export interface TooltipPlacementResult {
  placement: Exclude<TourStepPlacement, 'auto'>;
  top: number;
  left: number;
}

export function computeTooltipPlacement(args: {
  targetRect: RectLike;
  tooltipSize: { width: number; height: number };
  preferred: TourStepPlacement;
  viewportPadding?: number;
  gap?: number;
}): TooltipPlacementResult {
  const { targetRect, tooltipSize } = args;
  const preferred = args.preferred ?? 'auto';
  const pad = args.viewportPadding ?? 12;
  const gap = args.gap ?? 10;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const candidates: Array<Exclude<TourStepPlacement, 'auto'>> =
    preferred === 'auto'
      ? ['bottom', 'top', 'right', 'left']
      : preferred === 'top'
        ? ['top', 'bottom', 'right', 'left']
        : preferred === 'bottom'
          ? ['bottom', 'top', 'right', 'left']
          : preferred === 'left'
            ? ['left', 'right', 'bottom', 'top']
            : ['right', 'left', 'bottom', 'top'];

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const scoreFor = (placement: Exclude<TourStepPlacement, 'auto'>): { score: number; top: number; left: number } => {
    let top = 0;
    let left = 0;

    if (placement === 'bottom') {
      top = targetRect.bottom + gap;
      left = targetRect.left + targetRect.width / 2 - tooltipSize.width / 2;
    } else if (placement === 'top') {
      top = targetRect.top - gap - tooltipSize.height;
      left = targetRect.left + targetRect.width / 2 - tooltipSize.width / 2;
    } else if (placement === 'right') {
      top = targetRect.top + targetRect.height / 2 - tooltipSize.height / 2;
      left = targetRect.right + gap;
    } else {
      top = targetRect.top + targetRect.height / 2 - tooltipSize.height / 2;
      left = targetRect.left - gap - tooltipSize.width;
    }

    const clampedTop = clamp(top, pad, vh - pad - tooltipSize.height);
    const clampedLeft = clamp(left, pad, vw - pad - tooltipSize.width);

    // Score by how much clamping was needed (less is better), plus if it fits naturally.
    const clampPenalty = Math.abs(clampedTop - top) + Math.abs(clampedLeft - left);
    const fitsVert = top >= pad && top + tooltipSize.height <= vh - pad;
    const fitsHorz = left >= pad && left + tooltipSize.width <= vw - pad;
    const fitBonus = fitsVert && fitsHorz ? -200 : 0;
    return { score: clampPenalty + fitBonus, top: clampedTop, left: clampedLeft };
  };

  let best = { placement: candidates[0], top: pad, left: pad, score: Number.POSITIVE_INFINITY };
  for (const p of candidates) {
    const r = scoreFor(p);
    if (r.score < best.score) best = { placement: p, top: r.top, left: r.left, score: r.score };
  }

  return { placement: best.placement, top: best.top, left: best.left };
}
